import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert } from 'reactstrap';
import AuctionListTemplate, {
  AuctionListTemplateContextType,
} from '../common/components/new/AuctionListTemplate';
import AuctionListItem from '../common/components/new/AuctionListItem';
import { getFavouriteAuctions } from '../settings/SettingsActions';
import { AuctionSortType } from '../constants/AuctionSortType';
import { prepareSearchData, SORT_ASC, SORT_DESC } from '../common/helpers/prepareSearchData';
import { SSE_BUYER, SSEClient } from '../api/SSEClient';
import { getAuction, handleAuctionEvent } from '../myvehicles/MyVehiclesActions';

const sseClient = new SSEClient(SSE_BUYER);

function FavouriteOffersPage() {
  const dispatch = useDispatch();
  const [searchItems, setSearchItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [sort, setSort] = useState(AuctionSortType.ENDING_SOONEST);
  const latestCriteriaRef = useRef({});
  const [auctionsIds, setAuctionsIds] = useState([]);

  const handleItemRefresh = (newItem) => {
    if (!newItem || !newItem.id) return;
    setSearchItems((prev) =>
      Array.isArray(prev)
        ? prev.map((it) =>
            String(it?.id) === String(newItem.id) ? { ...it, ...newItem } : it,
          )
        : prev,
    );
  };

  // keep auctionsIds in sync with current favourites
  useEffect(() => {
    const ids = Array.isArray(searchItems)
      ? searchItems.map((it) => it?.id).filter(Boolean)
      : [];
    setAuctionsIds(ids);
  }, [searchItems]);

  useEffect(() => {
    sseClient.listen(auctionsIds, (response) => {
      dispatch(
        handleAuctionEvent(response, () => {
          if (response?.auctionId) {
            dispatch(
              getAuction(response.auctionId, (auction) => {
                handleItemRefresh(auction);
              }),
            );
          }
        }),
      );
    });
    return () => {
      sseClient.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionsIds, dispatch]);

  const fetchFavourites = async (params = {}) => {
    const sortVal = params.sort ?? sort;
    const sortOptions =
      sortVal === AuctionSortType.ENDING_SOONEST
        ? { endsOn: SORT_ASC }
        : { launchedOn: SORT_DESC };

    const combined = {
      offset: params.offset ?? offset,
      sort: sortVal,
      ...latestCriteriaRef.current,
      ...params,
    };

    try {
      const response = await dispatch(
        getFavouriteAuctions(prepareSearchData(combined, sortOptions)),
      );
      const content = response?.data?.data?.content || [];
      const total = response?.data?.data?.totalElements || 0;
      const last = response?.data?.data?.last ?? true;
      setHasMore(!last);
      setSearchItems((prev) =>
        combined.offset && combined.offset > 0 ? [...prev, ...content] : content,
      );
      return { total };
    } catch (error) {
      console.error('Error fetching favourite offers:', error);
      setHasMore(false);
      return { total: 0 };
    }
  };

  return (
    <AuctionListTemplate
      context={AuctionListTemplateContextType.FAVOURITE_OFFERS}
      searchItems={searchItems}
      hasMore={hasMore}
      searchAuctionList={(values) => {
        setOffset(0);
        return fetchFavourites({ ...values, offset: 0 });
      }}
      onLoadMore={() => {
        const next = offset + 1;
        setOffset(next);
        fetchFavourites({ offset: next });
      }}
      onCriteriaChange={(crit) => {
        latestCriteriaRef.current = crit || {};
      }}
      onSortChange={(newSort) => {
        setSort(newSort);
        setOffset(0);
        fetchFavourites({ offset: 0, sort: newSort });
      }}
      onItemRefresh={handleItemRefresh}
    >
      {searchItems.length === 0 ? (
        <div className="alert-wrapper">
          <Alert color="info">You don’t have any favorite offers yet.</Alert>
        </div>
      ) : (
        searchItems.map((item) => (
          <AuctionListItem
            key={item.id}
            item={item}
            refreshAuctionListCallback={() => fetchFavourites({ offset: 0 })}
            context={AuctionListTemplateContextType.FAVOURITE_OFFERS}
          />
        ))
      )}
    </AuctionListTemplate>
  );
}

export default FavouriteOffersPage;
